/**
 * web/src/hooks/mutations/useRegenerateGate.ts
 * Recompute the ops next-steps gate body from the reviewer's section picks.
 *
 * Unlike approve/skip, this does NOT remove the gate from the pending list or
 * advance the workflow — the gate stays waiting_approval. The caller reseeds
 * the editor from the returned { email, context } (a live draft wins over a
 * server refetch, F14), so this hook deliberately does NOT invalidate the
 * workflow query: a refetch could clobber the just-reseeded draft, and the
 * server has already re-persisted the same payload we hand back.
 */
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { regenerateGate, type RegenerateGateResult } from '../../api/gates';
import { toastApiError } from '../../lib/api';

/** Variables for the regenerate mutation. */
export interface UseRegenerateGateVars {
  workflowId: string;
  stepIndex: number;
  /** Chosen template sectionKey (the ops picker is single-select; the
   *  speaker card panel passes [] — its hook ignores the picks). */
  selected: string[];
  /** Card gate action: '' fresh drafts (default) | 'finalize' | 'build_card' |
   *  'back_to_final' | 'back'. */
  action?: string;
  /** Card gate finalize: the picked draft index. */
  chosenCandidate?: string;
  /** Card gate finalize: palette color name or 'random'. */
  highlightColor?: string;
  /** Card gate back/back_to_final: also retract what the advance published
   *  (the cards board's undo-toast path). */
  unpublish?: boolean;
  /** Speaker email gates: fresh redraft from current templates/defaults —
   *  discards the persisted draft. Nothing sent, no notification. */
  redraft?: boolean;
  /** Optional per-call toast override (e.g. "Fresh candidates generated"),
   *  mirroring GateMutationVars.successMessage. */
  successMessage?: string;
  /** Suppress the built-in success toast — the caller shows its own (the
   *  cards board's toast-with-Undo owns the message). Errors still toast. */
  silent?: boolean;
}

/**
 * Recomputes an ops gate's email from the reviewer's pick. Returns the fresh
 * { email, context } via the mutation result; the caller wires `onSuccess` to
 * reseed the form and lift the new context (rationale + picker) into state.
 *
 * @returns React Query mutation handle (RegenerateGateResult)
 * @sideEffects HTTP POST; server re-persists the gate (no workflow advance)
 * @idempotent yes — same pick yields the same body
 */
export function useRegenerateGate() {
  return useMutation<RegenerateGateResult, unknown, UseRegenerateGateVars>({
    mutationFn: (vars) =>
      regenerateGate(vars.workflowId, vars.stepIndex, {
        selected: vars.selected,
        ...(vars.action ? { action: vars.action } : {}),
        ...(vars.chosenCandidate ? { chosenCandidate: vars.chosenCandidate } : {}),
        ...(vars.highlightColor ? { highlightColor: vars.highlightColor } : {}),
        ...(vars.unpublish ? { unpublish: true } : {}),
        ...(vars.redraft ? { redraft: true } : {}),
      }),
    onSuccess: (_res, vars) => {
      if (vars.silent) return;
      toast.success(vars.successMessage ?? 'Email regenerated from your selection');
    },
    onError: (err) => {
      toastApiError(err, 'Could not regenerate the email');
    },
  });
}
